import type { UserLocation, PrayerTimes } from '../types';
import { CalculationMethod, Coordinates, Madhab, PrayerTimes as AdhanTimes } from 'adhan';

// Akurat berbasis koordinat (offline). Metode default: MuslimWorldLeague,
// mendekati jadwal Kemenag untuk Indonesia. Fallback ke jadwal statis bila gagal.
export function calculatePrayerTimes(location?: UserLocation, date: Date = new Date()): PrayerTimes {
  try {
    if (location && Number.isFinite(location.latitude) && Number.isFinite(location.longitude)) {
      const coords = new Coordinates(location.latitude, location.longitude);
      const params = CalculationMethod.MuslimWorldLeague();
      params.madhab = Madhab.Shafi;
      const times = new AdhanTimes(coords, date, params);
      return {
        fajr: formatDate(times.fajr),
        dhuhr: formatDate(times.dhuhr),
        asr: formatDate(times.asr),
        maghrib: formatDate(times.maghrib),
        isha: formatDate(times.isha),
      };
    }
  } catch (e) {
    console.log('Adhan calc failed, using fallback:', e);
  }

  // Fallback statis (sebelumnya)
  return {
    fajr: formatTime(4, 30),
    dhuhr: formatTime(12, 0),
    asr: formatTime(15, 30),
    maghrib: formatTime(18, 0),
    isha: formatTime(19, 30),
  };
}

function formatDate(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatTime(hour: number, minute: number): string {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

export async function getCurrentLocation(): Promise<UserLocation> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Get detailed location information using reverse geocoding
          const locationData = await getLocationDetails(latitude, longitude);
          resolve(locationData);
} catch {
          resolve({ 
            latitude, 
            longitude,
            city: 'Unknown Location',
            country: 'Unknown'
          });
        }
      },
      (_error) => reject(_error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  });
}

async function getLocationDetails(latitude: number, longitude: number): Promise<UserLocation> {
  try {
    // Using OpenStreetMap Nominatim API for reverse geocoding (free)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'PrayerTracker/1.0'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch location details');
    }
    
    const data = await response.json();
    
    // Extract relevant location information
    const address = data.address || {};
    const placeName = data.display_name?.split(',')[0] || 'Unknown Place';
    
    // Try to identify if it's a mosque or religious place
    const amenity = address.amenity;
    const building = address.building;
    const name = data.name;
    
    let locationName = placeName;
    if (amenity === 'place_of_worship' || building === 'mosque' || name?.toLowerCase().includes('masjid') || name?.toLowerCase().includes('mosque')) {
      locationName = name || placeName;
    }
    
    const city = address.city || address.town || address.village || address.suburb || 'Unknown City';
    const country = address.country || 'Unknown Country';
    const state = address.state || address.province || '';
    
    const fullAddress = [locationName, city, state, country].filter(Boolean).join(', ');
    
    return {
      latitude,
      longitude,
      city,
      country,
      placeName: locationName,
      fullAddress
    };
  } catch (error) {
    console.error('Error getting location details:', error);
    return {
      latitude,
      longitude,
      city: 'Unknown City',
      country: 'Unknown Country',
      placeName: 'Unknown Location'
    };
  }
}